import { FC, useState } from 'react';
import { isError } from '@belong/api';
import { applyDictionaryToString } from '@belong/contentful';
import { ALERT_POSITION, type IMoleculeAlert } from '@belong/types';
import { LIB_INPUTS_DEFAULT_IDS } from '../../../helpers/variables';
import Alert from '../../feedback/Alert';
import { GenericInput } from '../../forms/GenericInput';
import { RichText } from '../../styles/Typography';
import { Stack } from '../../ui';
import INPUT_CONTENT from './content.json';
import { ResendCodeButton } from './ResendCodeButton';
import { IVerificationCodeCaptureProps } from './types';

const DEFAULT_OTP_LENGTH = 6;

export const VerificationCodeCapture: FC<IVerificationCodeCaptureProps> = ({
  verificationCodeInput,
  resendCodeButton,
  resendSuccessfulAlert,
  resendErrorAlert,
  otpLength = DEFAULT_OTP_LENGTH,
  sendOtp,
  resendDelayInSeconds
}) => {
  const { errorMessages, label = '', hasFormError, manageForm, dictionary, ...extraProps } = verificationCodeInput;

  const { description, buttonLabel, countDownDescription } = resendCodeButton;

  const [alert, setAlert] = useState<IMoleculeAlert>();

  const errorValidation = { ...INPUT_CONTENT.errorMessages, ...errorMessages };

  const extendedDictionary = {
    otpLength,
    ...dictionary
  };

  const onResend = async () => {
    setAlert(undefined);

    try {
      const result = await sendOtp();
      if (isError(result)) {
        setAlert(resendErrorAlert);
      } else {
        setAlert(resendSuccessfulAlert);
      }
    } catch (exception) {
      setAlert(resendErrorAlert);
    }
  };

  if (resendDelayInSeconds !== undefined && resendDelayInSeconds < 30) {
    throw new Error('resendDelayInSeconds should be at least 30 seconds');
  }

  return (
    <Stack space={{ xs: 'small' }}>
      {alert && (
        <Alert key={alert.code} position={ALERT_POSITION.BLOCK} type={alert.level} hasFocus>
          <RichText {...alert.message} />
        </Alert>
      )}

      <GenericInput
        label={applyDictionaryToString(extendedDictionary, label)}
        hasFormError={false}
        manageForm={manageForm}
        errorValidation={[
          { errorKey: 'required', message: errorValidation.required },
          {
            errorKey: 'validFormat',
            message: errorValidation.invalid,
            pattern: new RegExp(`^(\\d{${otpLength}})$`)
          }
        ]}
        type="text"
        pattern="[0-9]*"
        inputMode="numeric"
        name={LIB_INPUTS_DEFAULT_IDS.VERIFICATION_CODE}
        dictionary={extendedDictionary}
        {...extraProps}
      />

      <ResendCodeButton
        description={applyDictionaryToString(extendedDictionary, description)}
        buttonLabel={buttonLabel}
        resendDelayInSeconds={resendDelayInSeconds}
        onResend={onResend}
        countDownDescription={countDownDescription}
        showResend={!alert}
        hasError={hasFormError || alert === resendErrorAlert}
      />
    </Stack>
  );
};

VerificationCodeCapture.displayName = 'VerificationCodeCapture';
